/** Event name constants for `template_analytics.name` — keep in sync with SQL RPC filters. */
export const TEMPLATE_ANALYTICS_EVENTS = {
  PAGE_VIEW: "page_view",
  NAV_CLICK: "Nav Click",
  POST_CLICK: "Post Click",
  SHOP_CLICK: "Redirect to shop link",
  ORIGINAL_POST: "Redirect to original post",
  QUICK_LINK: "Quick Link Click",
  BANNER_CTA: "Banner CTA Click",
  TABS_CLICK: "Tabs Click",
  SECTION_CLICK: "Section Click",
  CAROUSEL_TAB: "Carousel Tab Click",
  CONTENT_TAB: "Content Tab Click",
  CONTENT_VIEW_SWITCH: "Content View Switch",
  SHOP_TAB: "Shop Tab Click",
  SHOP_BROWSE: "Shop Browse Click",
  JOURNAL_POST: "Journal Post Click",
  SEARCH: "searchbar search",
  SOCIAL_LINK: "Social Link Click",
  HIGHLIGHT_CLICK: "Highlight Click",
  FEATURED_CAROUSEL_CLICK: "Featured Carousel Click",
  MORE_FROM_CLICK: "More From Click",
  NEWSLETTER_SUBMIT: "Newsletter Submit",
  CONTACT_SUBMIT: "Contact Submit",
  MOBILE_MENU: "Mobile Menu Open",
  JOURNAL_READ: "Journal Post Read",
  PAGE_SECTIONS_RENDERED: "Page Sections Rendered",
  PRODUCT_IMPRESSION: "Product Impression",
  PRESS_CLICK: "Press Item Click",
  BLOG_BACK: "Blog Back Click",
  HIGHLIGHT_CARD_CLICK: "Highlight Card Click",
  AUTHOR_CTA_CLICK: "Author CTA Click",
  ABOUT_DEEPLINK: "About Deep Link Click",
  CAROUSEL_ITEM_CLICK: "Carousel Item Click",
  SEARCH_CLEAR: "Search Clear",
  SEARCH_FOCUS: "Search Focus",
} as const;

export type TemplateEntityType =
  | "nav"
  | "link"
  | "post"
  | "product"
  | "carousel_tab"
  | "content_tab"
  | "content-series"
  | "shop_category"
  | "shop_button"
  | "banner"
  | "journal"
  | "search"
  | "social"
  | "highlight"
  | "form"
  | "section"
  | "press"
  | "carousel"
  | "author"
  | "highlight_card"
  | "about_value";

export function templateRouteName(username: string): string {
  const slug = String(username || "").trim().toLowerCase();
  return slug ? `/${slug}` : "";
}

/**
 * Session memory for URL-restored searches (`?term=&from=searchbar`).
 * Search chrome remounts while the feed loads; without this, one query is
 * inserted into `template_analytics` once per remount.
 */
const urlRestoredTemplateSearches = new Set<string>();

function templateSearchMemoryKey(username: string, query: string): string | null {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return null;
  const who = String(username || "").trim().toLowerCase();
  return `${who}:${q}`;
}

/** First URL restore of this term in this tab. Later remounts return false. */
export function consumeUrlRestoredTemplateSearch(
  username: string,
  query: string,
): boolean {
  const key = templateSearchMemoryKey(username, query);
  if (!key) return false;
  if (urlRestoredTemplateSearches.has(key)) return false;
  urlRestoredTemplateSearches.add(key);
  return true;
}

/** Call from Go/Enter so a following URL-restore effect does not count again. */
export function rememberUrlRestoredTemplateSearch(
  username: string,
  query: string,
): void {
  const key = templateSearchMemoryKey(username, query);
  if (!key) return;
  urlRestoredTemplateSearches.add(key);
}
